import { useContext, useEffect, useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Customized,
} from "recharts";
import { getBarChart } from "../../service/AnalyticsService";
import AugmentoIcon from "./SVGComponents/AugmentoIcon";
import Select from "react-select";
import WidgetSelectStyles from "./WidgetSelectStyles";
import { API_BASE_URL, APIs, AuthRoutes } from "../../constants/Constants";
import { useNavigate } from "react-router-dom";
import DownArrowIcon from "./DownArrowIcon";
import OpenInNewIcon from "./OpenInNewIcon";
import OpenWidgetHeader from "./OpenWidgetHeader";
import { getModalityList } from "../../service/MasterService";
import userContext from "../../context/users/UserContext";

const normalityColorMap = {
  normal: {
    avg: "#1abc9c", // Teal (positive)
    minMax: "#a3e4d7", // Soft teal
  },
  abnormal: {
    avg: "#e74c3c", // Strong coral red (negative)
    minMax: "#f1948a", // Light coral
  },
  total: {
    avg: "#fd8d3c",
    minMax: "#5dade2",
  },
  null: {
    avg: "#fd8d3c",
    minMax: "#5dade2",
  },
};

const CustomTooltip = ({ active, payload, label, colorSet, chartType }) => {
  if (!active || !payload || payload.length === 0) return null;

  // payload[0] is Average TAT line point
  const { payload: dataPoint } = payload[0];

  const formatTAT = (value) => {
    return chartType === "radiologist_tat_trend"
      ? formatSecondsToMMSS(value)
      : value;
  };

  return (
    <div
      style={{
        background: "white",
        padding: "6px 10px",
        borderRadius: "6px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
        fontSize: "10px",
        lineHeight: "1.6",
        color: "#333",
      }}
    >
      <div>
        <strong>{formatDateDDMMMYY(label)}</strong>
      </div>
      {dataPoint.avg != null && (
        <div style={{ color: colorSet.avg }}>
          <strong>Avg TAT: {formatTAT(dataPoint.avg)}</strong>
        </div>
      )}
    </div>
  );
};

const formatSecondsToMMSS = (seconds) => {
  const mm = Math.floor(seconds / 60);
  const ss = Math.floor(seconds % 60);
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const formatDateDDMMMYY = (value) => {
  const date = new Date(value);
  if (isNaN(date)) return value; // fallback if invalid date

  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

const CustomXAxisLabel = ({ viewBox }) => {
  const { x, y, width } = viewBox;

  return (
    <text
      x={x + width / 2}
      y={y + 35}
      textAnchor="middle"
      fill="#ccc"
      fontSize={10}
      fontWeight="bold"
    >
      <tspan x={x + width / 2} dy={0}>
        Reported Date
      </tspan>
      <tspan x={x + width / 2} dy={15} fontSize={10} fontWeight="normal">
        (DD-MMM-YYYY)
      </tspan>
    </text>
  );
};

const CustomYAxisLabel = ({ viewBox, chartType }) => {
  const { x, y, height } = viewBox;
  const unit = chartType === "radiologist_tat_trend" ? "mm:ss" : "seconds";

  return (
    <text
      x={x - 55}
      y={y + height / 2 + 60}
      textAnchor="middle"
      fill="#ccc"
      fontSize={10}
      transform={`rotate(-90, ${x - 50}, ${y + height / 2})`}
    >
      <tspan fontWeight="bold">TAT</tspan>
      <tspan fontWeight="normal"> ({unit})</tspan>
    </text>
  );
};
const mockTATData = [
  { x: "2025-06-16", min: 90, avg: 150, max: 210 },
  { x: "2025-06-17", min: 120, avg: 180, max: 240 },
  { x: "2025-06-18", min: 100, avg: 160, max: 220 },
  { x: "2025-06-19", min: 95, avg: 155, max: 205 },
  { x: "2025-06-20", min: 110, avg: 170, max: 230 },
  { x: "2025-06-21", min: 85, avg: 140, max: 195 },
  { x: "2025-06-22", min: 130, avg: 190, max: 250 },
];

const formatDateToDDMMMYYYY = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const AnalyticsPage = ({
  data = mockTATData,
  normality,
  chartType,
  setShowAnalyticsPanel,
  studyId,
  firmId,
  userName,
  modalityName
}) => {
  const navigate = useNavigate();
  const {username} = useContext(userContext);
  const [chartMode, setChartMode] = useState("study_scan_tat_trend");
  const [chartData, setChartData] = useState([]);

  const chartOptions = [
    { key: "study_scan_tat_trend", text: "Scan to Report" },
    { key: "study_tat_trend", text: "Upload to Report" },
    { key: "assignment_tat_trend", text: "Assignment to Report" },
    { key: "radiologist_tat_trend", text: "Open to Report" },
  ];

   let today;
    if (username === "jsmith") {
      today = new Date(2026, 4, 28);
    } else {
      today = new Date();
    }
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 7);

    const dateFrom = formatDateToDDMMMYYYY(pastDate);
    const dateTo = formatDateToDDMMMYYYY(today);

  const fetchChartData = async (chartTypeParam) => {
    let modalityIds = [];
    try {
      let mdl = [];
      const response = await getModalityList();
      if (response.status === 200) {
                response?.data?.data?.modalityList.map((modality) => {
          mdl.push({
            key: modality.id,
            text: modality.name,
          });
        });
      }
      
      const allowedModalities = ['CR', 'DX', 'RG'];

      if (allowedModalities.includes(modalityName)) {
        const selectedModality = mdl.find(
          (modality) => modality.text === modalityName
        );
        if (selectedModality) {
          modalityIds = [selectedModality.key];
        }
      }
    } catch (error) {
      console.error("Failed to fetch chart data", error);
    }

    const finalPayload = {
      withCredentials: true,
      chartConfigDto: {
        chartType: chartTypeParam,
        filter: "upload_date",
        dateFrom,
        dateTo,
        timeFrom: null,
        timeTo: null,
        selfTat: 0,
        fullDay: false,
        normality: "total",
        // ...(modalityIds.length > 0 && { modalityIds }),
      },
    };
   

    try {
      const response = await getBarChart(finalPayload);

      if (response.status === 200) {
        const data = response.data.data;

        let chartData =
          data.chartData && Object.keys(data.chartData).length > 0
            ? data.chartData
            : data.sortedChartData || {};

        const objectEntries = Object.entries(chartData);

        const rechartsData = [];

        for (let [key, value] of objectEntries) {
          const date = key;
          let min = null,
            avg = null,
            max = null;

          value.forEach((entryObj) => {
            for (const [label, val] of Object.entries(entryObj)) {
              if (label === "Minimum TAT") min = val;
              else if (label === "Average TAT") avg = val;
              else if (label === "Maximum TAT") max = val;
            }
          });

          rechartsData.push({
            x: date,
            min: min ?? null,
            avg: avg ?? null,
            max: max ?? null,
          });
        }

        setChartData(rechartsData);
      }
    } catch (error) {
      console.error("Failed to fetch chart data", error);
    }
  };

  useEffect(() => {
    fetchChartData("study_scan_tat_trend");
  }, []);

  useEffect(() => {
    fetchChartData(chartMode);
  }, [chartMode]);

  const colorSet = useMemo(() => {
    return normalityColorMap[normality || "null"];
  }, [chartData]);

  const [paddedMin, paddedMax] = useMemo(() => {
    const allMins = chartData.map((d) => d.min).filter((v) => v != null);
    const allMaxs = chartData.map((d) => d.max).filter((v) => v != null);

    const globalMin = Math.min(...allMins);
    const globalMax = Math.max(...allMaxs);

    const range = globalMax - globalMin || 1;
    const padding = range * 0.1;

    // 👇 Clamp minimum to 0
    const safeMin = Math.max(0, Math.floor(globalMin - padding));
    const safeMax = Math.ceil(globalMax + padding);

    return [safeMin, safeMax];
  }, [chartData]);

  const paddingSize =
    data.length === 2
      ? 200
      : data.length === 3
      ? 120
      : data.length === 4
      ? 70
      : data.length === 5
      ? 60
      : 40;

  const handleOpenAnalyticsWindow = async () => {
    let modalityIds = [];

      try {
        let mdl = [];
        const response = await getModalityList();

        if (response.status === 200) {
          response?.data?.data?.modalityList.forEach((modality) => {
            mdl.push({
              key: modality.id,
              text: modality.name,
            });
          });
        }

        const allowedModalities = ['CR', 'DX', 'RG'];

        if (allowedModalities.includes(modalityName)) {
          const selectedModality = mdl.find(
            (modality) => modality.text === modalityName
          );

          if (selectedModality) {
            modalityIds = [selectedModality.key];
          }
        }
      } catch (error) {
        console.error("Failed to fetch chart data", error);
      }
    handleOpenViewerWindow(studyId, firmId, userName, false,false,true,chartMode, dateFrom, dateTo, modalityIds);
  };

  const handleOpenViewerWindow = async (studyId, firmId, userName, isReportClicked,isAugDemo,isProductivityClicked, chartMode, chartDateFrom, chartDateTo, modalityIds) => {
      try {
        let response = null;
        if(isAugDemo){
          response = await fetch(`https://augemto-demo.deeptek.ai:8002/gizmo/generate-access-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ studyId, firmId, isReportClicked,isProductivityClicked,chartMode, chartDateFrom, chartDateTo, modalityIds }),
        });
        }
        else{
          response = await fetch(`${API_BASE_URL}jwtServices/gizmo/generate-access-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ studyId, firmId, isReportClicked,isProductivityClicked,chartMode,chartDateFrom, chartDateTo, modalityIds }),
        });
        }
        
  
        if (!response.ok) {
          const error = await response.text();
          console.error("Failed to generate access code:", error);
          alert("Unable to open viewer.");
          return;
        }
  
        const data = await response.json();
        const accessCode = data.accessCode;
        let viewerUrl = null;
        if(isAugDemo){
          viewerUrl = `https://augmento-demo.deeptek.ai/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
        }
        else{
          viewerUrl = `${window.location.origin}/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
        }
        window.parent.postMessage({ type: "open-external", url: viewerUrl }, "*");
      } catch (err) {
        console.error("Error in handleOpenViewerWindow:", err);
        alert("Something went wrong while opening the viewer.");
      }
    };

  return (
    <>
      <OpenWidgetHeader
        options={chartOptions.map((opt) => ({
          value: opt.key,
          label: opt.text,
        }))}
        selectedValue={chartMode}
        onChangeValue={setChartMode}
        SelectStyles={WidgetSelectStyles}
        leftSelectWidth="150px"
        rightIcons={[
          {
            key: 'open-analytics',
            onClick: handleOpenAnalyticsWindow,
            Icon: OpenInNewIcon,
            style: { marginLeft: '0px' }, // removes initial margin from first icon
          },
          {
            key: 'close-analytics',
            onClick: () => setShowAnalyticsPanel(false),
            Icon: DownArrowIcon,
          },
        ]}
      />

      <div style={{height:"198px"}}>
        <ResponsiveContainer >
          {/* <ComposedChart data={data} > */}
          <ComposedChart
            data={chartData}
            margin={{ top: 15, right: 10, bottom: 10, left: 5 }}
          >
            <CartesianGrid stroke="#555" strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              label={<CustomXAxisLabel />}
              stroke="#555"
              tick={{
                fill: "#ccc",
                fontSize: 10,
                //  angle: 45, dy: 22, dx: 18
              }}
              padding={{ left: paddingSize, right: paddingSize }}
              minTickGap={20}
              interval={data.length > 6 ? 1 : 0}
              tickFormatter={formatDateDDMMMYY}
            />
            <YAxis
              domain={[paddedMin, paddedMax]}
              label={<CustomYAxisLabel chartType={chartType} />}
              stroke="#555"
              tick={{ fill: "#ccc", fontSize: 10 }}
              tickCount={8}
              tickFormatter={
                chartType === "radiologist_tat_trend"
                  ? formatSecondsToMMSS
                  : undefined
              }
            />
            <Tooltip
              content={
                <CustomTooltip colorSet={colorSet} chartType={chartType} />
              }
              cursor={false}
            />
            <Line
              type="monotone"
              dataKey="avg"
              name="Average TAT"
              stroke={colorSet.avg}
              strokeWidth={3}
              dot={{ r: 4, fill: colorSet.avg, strokeWidth: 2 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
       {/* <div style={{ position: "relative", height: "34px", backgroundColor: "#0C1A2C", zIndex: 1000 }}>
            <div
              style={{
                position: "absolute",
                // bottom: 4,
                right: 10,
                // display: "flex",
                // flexDirection: "row",
                // alignItems: "center",
                paddingTop: "2px",
              }}
            >
            <AugmentoIcon />
          </div>
        </div> */}
    </>
  );
};

export default AnalyticsPage;
